import type { IObservableArray } from 'mobx';
import numeral from 'numeral';

import type {
  Campaign,
  Segments as SegmentsCollection,
  Targetable,
  Targetables as TargetablesCollection,
  Targeting,
} from '@feathr/blackbox';
import {
  AffinityTargetable,
  AUDIENCE_TARGETING_KINDS,
  CampaignClass,
  CampaignState,
  FILTER_TARGETING_KINDS,
  Segment,
  TargetableClass,
} from '@feathr/blackbox';
import type { TValidateGrouped } from '@feathr/rachis';

import type { IUniqueEmailsResult } from '../AddWizard.useReconcileUniqueEmails';
import type { IReachValidationResult } from './AdWizardAudienceStep.useReachValidation';

/**
 * Hard minimum reachable audience on the display/Trade Desk channel, gating Segment, Omni,
 * Omni Reach, and Affinity (email mapping uses its own unique-email floor).
 */
export const MIN_REACHABLE_AUDIENCE = 2000;

/**
 * Hard minimum reachable audience on the Meta channel, enforced independently of the display
 * minimum for campaigns that serve on Meta (Omni / Omni Reach with the Meta channel selected).
 */
export const MIN_META_REACHABLE_AUDIENCE = 2000;

/**
 * Sum the `unique_user_count` of an affinity campaign's selected audiences. Non-affinity
 * targetables are ignored.
 *
 * @param targetables - The resolved targetable models for the campaign.
 * @returns The combined reachable people across affinity audiences.
 */
export function getAffinityAudienceSize(targetables: Array<Targetable | Segment>): number {
  return targetables
    .filter(
      (targetable): targetable is AffinityTargetable => targetable instanceof AffinityTargetable,
    )
    .reduce((total, targetable) => total + (targetable.get('unique_user_count') ?? 0), 0);
}

/**
 * Which active channels fall below their reachable-audience minimum. A channel only counts as
 * short when the campaign actually serves on it.
 *
 * @param reachValidation - Per-channel reach counts and which channels apply.
 * @returns Whether the display and/or Meta channel is below its minimum.
 */
export function getReachShortfall(reachValidation: IReachValidationResult): {
  displayShort: boolean;
  metaShort: boolean;
} {
  return {
    displayShort:
      reachValidation.appliesToDisplay && reachValidation.displayReach < MIN_REACHABLE_AUDIENCE,
    metaShort:
      reachValidation.appliesToMeta && reachValidation.metaReach < MIN_META_REACHABLE_AUDIENCE,
  };
}

/**
 * Build the gate error naming the channel(s) below the minimum. Channels are named only when the
 * campaign serves on more than one (so single-channel campaigns read as "Your audience").
 *
 * @param displayShort - Whether the display channel is below its minimum.
 * @param metaShort - Whether the Meta channel is below its minimum.
 * @param appliesToMeta - Whether the campaign serves on the Meta channel at all.
 * @returns The gate error message.
 */
export function getReachMinimumError(
  displayShort: boolean,
  metaShort: boolean,
  appliesToMeta: boolean,
): string {
  const minimum = numeral(MIN_REACHABLE_AUDIENCE).format('0,0');
  if (displayShort && metaShort) {
    return `Your Display and Meta audiences must each be able to reach at least ${minimum} people. Add or broaden your targets to continue.`;
  }
  if (metaShort) {
    return `Your Meta audience must be able to reach at least ${minimum} people. Add or broaden your targets to continue.`;
  }
  // Only name the display channel when Meta is also in play; otherwise it's the only audience.
  const audience = appliesToMeta ? 'Display audience' : 'audience';
  return `Your ${audience} must be able to reach at least ${minimum} people. Add or broaden your targets to continue.`;
}

interface ITargetingsErrors extends TValidateGrouped {
  all?: string[];
  kind?: string[];
  target_data?: string[];
}
interface ITargetablesErrors extends TValidateGrouped {
  all?: string[];
  radius?: string[];
  units?: string[];
}

interface IErrors extends TValidateGrouped {
  targetings: ITargetingsErrors[];
  targetables: ITargetablesErrors[];
}

interface IValdateStepTargetsProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
  segments: Segment[];
  targetables: Array<Targetable | Segment>;
  emailValidation: IUniqueEmailsResult;
  /**
   * Reachable-audience size used to enforce the minimum-audience gate. Optional: when omitted
   * (e.g. Facebook/Meta campaigns), the reachable-audience minimum is not enforced.
   */
  reachValidation?: IReachValidationResult;
  /**
   * When true, the minimum reachable-audience gate is skipped. Internal accounts are exempt
   * everywhere except production, so the campaign minimum is still enforced for them on prod.
   */
  isReachMinimumExempt?: boolean;
}

export function validateStepTargets({
  campaign,
  targetings,
  segments,
  targetables: targetablesProp,
  emailValidation,
  reachValidation,
  isReachMinimumExempt,
}: IValdateStepTargetsProps): IErrors {
  const targetables = targetablesProp;
  const errorMessages = {
    targetings: [],
    targetables: [{ units: [], radius: [] }],
  } as IErrors;
  const isNotDraft = campaign.get('state') !== CampaignState.Draft;
  const hasChanges = targetings.some((t) => t.isDirty || t.isEphemeral);
  const {
    isEmailMappingCampaign,
    isSegmentCampaign,
    isFacebook,
    isOmniReachCampaign,
    isAffinityCampaign,
  } = campaign;

  if (isNotDraft && !hasChanges) {
    return errorMessages;
  }

  let audienceTargetingKinds = AUDIENCE_TARGETING_KINDS;

  // Use filters as audience targeting for awareness pixl plus campaigns
  if (isOmniReachCampaign) {
    audienceTargetingKinds = FILTER_TARGETING_KINDS;
  }

  const validatedTargetings = targetings.filter(
    (t) => !t.get('is_archived') && audienceTargetingKinds.includes(t.get('kind')),
  );

  if (validatedTargetings.length === 0) {
    errorMessages.targetings.push({ all: ['Please add at least one Target.'] });
  }

  /*
   * Hard 2,000 reachable-audience floor, enforced per channel: Display and Meta each have their
   * own minimum, so a campaign serving on both must clear both. Segment/Omni use the audience
   * breakdown, Omni Reach the channel forecast, and Affinity (display-only) the selected
   * audiences' person counts. Email mapping has its own unique-email floor; types with no
   * reachable number (Search, Lookalike, Seed Segment, geofencing) aren't gated. Skipped when
   * exempt or with no targets.
   */
  const usesReachValidation =
    (isSegmentCampaign && !isOmniReachCampaign && !isEmailMappingCampaign) || isOmniReachCampaign;
  const affinityBelowMinimumError = `Your audience must be able to reach at least ${numeral(
    MIN_REACHABLE_AUDIENCE,
  ).format('0,0')} people. Add or broaden your targets to continue.`;

  if (!isReachMinimumExempt && validatedTargetings.length > 0) {
    if (usesReachValidation && reachValidation) {
      const { displayShort, metaShort } = getReachShortfall(reachValidation);
      if (reachValidation.isLoading) {
        errorMessages.targetings.push({ all: ['Calculating reachable audience…'] });
      } else if (displayShort || metaShort) {
        errorMessages.targetings.push({
          all: [getReachMinimumError(displayShort, metaShort, reachValidation.appliesToMeta)],
        });
      }
    } else if (isAffinityCampaign) {
      const hasSelectedAffinityAudience = targetables.some((t) => t instanceof AffinityTargetable);
      if (
        hasSelectedAffinityAudience &&
        getAffinityAudienceSize(targetables) < MIN_REACHABLE_AUDIENCE
      ) {
        errorMessages.targetings.push({ all: [affinityBelowMinimumError] });
      }
    }
  }

  // Only segment targetings require at least one included targeting
  const includedSegmentTargetings = validatedTargetings.filter(
    (t) => t.get('kind') === 'segment' && t.get('included'),
  );

  if (includedSegmentTargetings.length === 0 && isSegmentCampaign && !isOmniReachCampaign) {
    errorMessages.targetings.push({
      all: ['At least one Target must be "included" rather than "excluded".'],
    });
  }

  validatedTargetings.forEach((t) => {
    errorMessages.targetings.push(
      t.validate<ITargetingsErrors>(['target_data', 'kind'], false, 'grouped').errors,
    );
  });
  if (isFacebook && isSegmentCampaign) {
    if (
      // TODO: make sure this didn't break anything
      segments
        ?.flatMap((trgt) => (trgt instanceof Segment ? trgt.get('predicates') : []))
        .some((pred) => !!pred && pred.attr_against !== 'loc_url')
    ) {
      errorMessages.targetables.push({
        all: ['Meta campaign targets can only use the "URL" filter.'],
      });
    }
  }

  // Check for duplicate email addresses across csvs and groups for email mapping campaigns
  if (
    campaign.isEmailMappingCampaign &&
    (emailValidation?.isLoading || !emailValidation?.hasEnoughUniqueEmails)
  ) {
    errorMessages.targetings.push({
      all: [
        emailValidation?.isLoading
          ? 'Checking for duplicate email addresses...'
          : `Need ${numeral(emailValidation?.remainingEmails).format('0,0')} more unique email addresses`,
      ],
    });
  }

  if (
    !isSegmentCampaign &&
    !isEmailMappingCampaign &&
    targetables &&
    validatedTargetings.length !== targetables.length
  ) {
    errorMessages.targetables.push({
      // TODO: fix the error message about this with adding segments
      all: ['One or more targets are incomplete.'],
    });
  }
  if (targetables) {
    targetables.forEach((t: Segment | Targetable) => {
      if (t.isErrored) {
        errorMessages.targetables.push({
          all: ['Target is invalid.'],
        });
      } else {
        errorMessages.targetables.push(t.validate<ITargetablesErrors>([], false, 'grouped').errors);
      }
    });
  }
  return errorMessages;
}

export function getTargetSegments(
  targetings: IObservableArray<Targeting>,
  Segments: SegmentsCollection,
): Segment[] {
  return targetings
    .filter(
      (t) => t.get('kind') === 'segment' && !!t.get('target_data') && t.get('is_archived') !== true,
    )
    .map((t) => Segments.get(t.get('target_data')!));
}

/**
 * Map of campaign types that use 'lookalike' targeting kind to their actual TargetableClass.
 * Multiple campaign types share the 'lookalike' targeting kind but require different targetable classes.
 */
const LOOKALIKE_TARGETABLE_CLASS: Partial<Record<CampaignClass, TargetableClass>> = {
  [CampaignClass.Affinity]: TargetableClass.affinity,
  [CampaignClass.Lookalike]: TargetableClass.lookalike,
  [CampaignClass.SeedSegment]: TargetableClass.seed_segment,
};

export function getEphemeralTargetables(
  targetings: IObservableArray<Targeting>,
  Targetables: TargetablesCollection,
  campaign?: Campaign,
): Targetable[] {
  // Determine the correct _cls for lookalike targetings based on campaign type
  const campaignCls = campaign?.get('_cls');
  const lookalikeTargetableClass = campaignCls
    ? LOOKALIKE_TARGETABLE_CLASS[campaignCls]
    : undefined;

  return targetings
    .filter(
      (t) =>
        [
          'email_list',
          'geo_audience',
          'geofence',
          'lookalike',
          'search',
          'omni_lookalike',
        ].includes(t.get('kind')) &&
        !!t.get('target_data') &&
        !t.get('is_archived'),
    )
    .map((t) => {
      const targetableId = t.get('target_data')!;
      /*
       * Multiple campaign types (Affinity, Lookalike, SeedSegment) share the 'lookalike'
       * targeting kind but require different Targetable subclasses. Without passing the
       * correct _cls, the Collection.get() call creates a generic Targetable (or wrong
       * subclass) which gets cached. When the server response arrives with the actual _cls,
       * rachis detects the mismatch and replaces the model, but by then the UI may already
       * hold a reference to the wrong model, causing "X is not a function" errors.
       *
       * By passing the correct _cls upfront based on campaign type, we ensure the model
       * is created with the right subclass from the start.
       */
      if (t.get('kind') === 'lookalike' && lookalikeTargetableClass) {
        return Targetables.get(targetableId, {
          _cls: lookalikeTargetableClass as
            | TargetableClass.affinity
            | TargetableClass.lookalike
            | TargetableClass.seed_segment,
        });
      }
      return Targetables.get(targetableId);
    })
    .filter((t) => t.isDirty || t.isEphemeral || !t.isPending);
}
